import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import {
  type LegacyMinRecordItem,
  type LegacyOnTableRowClickArgs,
  type LegacyRowActionsConfigProp,
  LegacyTable,
  type LegacyTableProps,
} from '@ctw/shared/components/tables/legacy-table';
import { twx } from '@ctw/shared/utils/tailwind';
import { type ReactElement, useRef } from 'react';
import { useToggleRead } from '../hooks/use-toggle-read';
import { useAdditionalResourceActions } from './use-additional-resource-actions';
import './resource-table.scss';
import type { Breakpoint } from '@ctw/config/zui';
import { DEFAULT_PAGE_SIZE } from '@ctw/shared/components/pagination/expand-list';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import type { Resource } from 'fhir/r4';

export type ResourceTableProps<T extends LegacyMinRecordItem> = {
  title: string;
  titleVisuallyHidden: boolean;
  className?: string;
  columns: LegacyTableProps<T>['columns'];
  data: Array<T>;
  emptyMessage?: string | ReactElement;
  isLoading?: boolean;
  onRowClick?: LegacyTableProps<T>['handleRowClick'];
  rowActions?: (r: T) => LegacyRowActionsConfigProp<T>;
  onCmdOrCtrlClick?: LegacyTableProps<T>['onCmdOrCtrlClick'];
  showTableHead?: boolean;
  enableDismissAndReadActions?: boolean;
  hidePagination?: boolean;
  stacked?: boolean;
  stackedBreakpoint?: Breakpoint;
};

export const ResourceTable = <T extends Resource, M extends FHIRModel<T>>({
  title,
  titleVisuallyHidden = false,
  className,
  columns,
  data,
  emptyMessage,
  isLoading = false,
  onRowClick,
  onCmdOrCtrlClick,
  rowActions,
  showTableHead,
  enableDismissAndReadActions,
  stacked = false,
  hidePagination = false,
  stackedBreakpoint = 'sm',
}: ResourceTableProps<M>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { requestContext } = useCTW();
  const { breakpoints } = useContainerQuery({ containerRef });

  const { toggleRead } = useToggleRead();

  // Create the RowActions react node
  const RowActionsWithAdditions = useAdditionalResourceActions({
    rowActions,
    enableDismissAndReadActions,
  });

  const onRowClickWithRead = onRowClick
    ? ({ telemetry, record }: LegacyOnTableRowClickArgs<M>) => {
        if (
          !record.isRead &&
          enableDismissAndReadActions &&
          !record.ownedByBuilder(requestContext.builderId)
        ) {
          void toggleRead(record);
        }
        onRowClick({ telemetry, record });
      }
    : undefined;

  return (
    <div
      ref={containerRef}
      data-testid="resource-table"
      className={twx(className, 'resource-table')}
    >
      <LegacyTable
        title={title}
        titleVisuallyHidden={titleVisuallyHidden}
        getRowClassName={(record) =>
          twx({
            'tr-dismissed': record.isDismissed,
            'tr-unread':
              enableDismissAndReadActions &&
              !record.ownedByBuilder(requestContext.builderId) &&
              !record.isRead,
          })
        }
        showTableHead={showTableHead}
        stacked={stacked || breakpoints.isAtMost[stackedBreakpoint]}
        emptyMessage={emptyMessage}
        loading={isLoading}
        records={data}
        RowActions={RowActionsWithAdditions}
        columns={columns}
        handleRowClick={(args) => {
          if (onRowClickWithRead) {
            void onRowClickWithRead(args);
          }
        }}
        onCmdOrCtrlClick={onCmdOrCtrlClick}
        pagination={hidePagination ? undefined : { type: 'show-more', pageSize: DEFAULT_PAGE_SIZE }}
      />
    </div>
  );
};
